import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAppStore from '../../store';
import { scheduledJobs as sampleJobs, autonomyLedger } from '../../mocks/scheduler';
import { listSchedulerJobs, rescheduleJob, updateJobCron } from '../../lib/api/scheduler';
import DataModeBadge from '../../components/feedback/DataModeBadge';
import TimelineView from './views/TimelineView';
import CalendarHeatmapView from './views/CalendarHeatmapView';
import AutonomyLogView from './views/AutonomyLogView';
import NextRunSummary from './components/NextRunSummary';
import CronEditor from './components/CronEditor';

const VIEWS = [
  { id: 'timeline', label: 'Timeline' },
  { id: 'heatmap', label: 'Density' },
  { id: 'ledger', label: 'Autonomy log' }
];

// Sample fallbacks for the panels whose backend source does not exist yet —
// shown ONLY behind the honest sample label, never silently.
const SAMPLE_HEATMAP_NOTE = 'Density view uses sample data — run-history source pending';

// Scheduler container (T7.1): view switcher + optimistic mutations with
// rollback. Reads are fetch-first (cutover §3): GET /api/scheduler/jobs on
// mount; success flips mode 'live' with the server rows (empty array = live
// and empty — the sample seed is NOT reinstated on a real empty list, per the
// useAgents precedent); failure keeps the sample seed behind the labeled
// 'sample' mode. Drag/cron edits PATCH the real row and roll back the local
// change on error — sample-mode edits still attempt the PATCH and roll back
// identically (an id that does not exist server-side rejects, restoring the
// sample value).
function SchedulerPage() {
  const [jobs, setJobs] = useState(sampleJobs);
  const [mode, setMode] = useState('sample');
  const [activeView, setActiveView] = useState('timeline');
  const [editingJobId, setEditingJobId] = useState(null);
  const agents = useAppStore((state) => state.agents);

  useEffect(() => {
    let cancelled = false;
    listSchedulerJobs()
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setJobs(list);
        setMode('live');
      })
      .catch(() => {
        // Backend unreachable — sample seed stays, honestly labeled.
        if (cancelled) return;
        setJobs(sampleJobs);
        setMode('sample');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const patchJobOptimistic = useCallback((jobId, changes, persist) => {
    let snapshot;

    setJobs((previous) => previous.map((job) => {
      if (job.id !== jobId) {
        return job;
      }

      snapshot = job;
      return { ...job, ...changes };
    }));

    persist().catch(() => {
      // Server rejection — roll back to the pre-mutation snapshot.
      if (snapshot) {
        setJobs((previous) => previous.map((job) => (
          job.id === jobId ? snapshot : job
        )));
      }
    });
  }, []);

  const handleReschedule = useCallback((jobId, newStartHour) => {
    patchJobOptimistic(
      jobId,
      { startHour: newStartHour },
      () => rescheduleJob(jobId, newStartHour)
    );
  }, [patchJobOptimistic]);

  const handleSaveCron = useCallback((jobId, cron) => {
    patchJobOptimistic(jobId, { cron }, () => updateJobCron(jobId, cron));
    setEditingJobId(null);
  }, [patchJobOptimistic]);

  const editingJob = useMemo(
    () => jobs.find((job) => job.id === editingJobId) || null,
    [jobs, editingJobId]
  );

  return (
    <main className="page-content scheduler-page">
      <header className="scheduler-head">
        <div>
          <p className="eyebrow">AUTONOMY CONTROL</p>
          <h1>
            Scheduler{' '}
            <DataModeBadge
              mode={mode}
              label={mode === 'live' ? null : 'Sample jobs — live schedule pending backend seed'}
            />
          </h1>
          <span className="scheduler-sub">
            Coordinate scheduled work, recurring jobs, and autonomy windows
            across {agents.length} agents.
          </span>
        </div>
        <div aria-label="Scheduler views" className="view-switcher" role="tablist">
          {VIEWS.map((view) => (
            <button
              aria-selected={activeView === view.id}
              className={activeView === view.id ? 'is-active' : ''}
              key={view.id}
              onClick={() => setActiveView(view.id)}
              role="tab"
              type="button"
            >
              {view.label}
            </button>
          ))}
        </div>
      </header>

      {activeView === 'timeline' && (
        <>
          <NextRunSummary jobs={jobs} />
          <TimelineView
            jobs={jobs}
            onEditJob={setEditingJobId}
            onReschedule={handleReschedule}
          />
          {editingJob && (
            <CronEditor
              job={editingJob}
              onCancel={() => setEditingJobId(null)}
              onSave={handleSaveCron}
            />
          )}
        </>
      )}

      {activeView === 'heatmap' && <CalendarHeatmapView />}

      {activeView === 'ledger' && <AutonomyLogView ledger={autonomyLedger} />}

      {activeView === 'heatmap' && (
        <p className="settings-note" role="note">{SAMPLE_HEATMAP_NOTE}</p>
      )}
      {activeView === 'ledger' && (
        <p className="settings-note" role="note">
          Autonomy log is sample data — decision-ledger source pending (outcomes loop, T2)
        </p>
      )}
    </main>
  );
}

export default SchedulerPage;
